import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import { AutocompleteFreeSoloValueMapping, Theme } from '@mui/material';
import MuiAutocomplete, { AutocompleteProps, AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import type { PopperProps } from '@mui/material/Popper';
import React, { useContext } from 'react';
import styled from 'styled-components';
import { Chip, PaletteColors } from '../chip/index';
import { CircularProgress } from '../circular-progess/index';
import { useMuiClickToDismiss } from '../mui-click-to-dismiss/index';
import { ClickToDismissPortalContext, ContainerContext } from '../popover/context';
import { ExtendedTextFieldProps, TextField, TextFieldStatus } from '../text-field/index';

export interface SelectProps<
	T = unknown,
	Multiple extends boolean | undefined = undefined,
	DisableClearable extends boolean | undefined = undefined,
	FreeSolo extends boolean | undefined = undefined
> extends Omit<AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>, 'renderInput' | 'renderTags'>,
		Pick<ExtendedTextFieldProps, 'label' | 'helperText' | 'startAdornment' | 'required' | 'endAdornment' | 'inputRef'> {
	/** The component variant. Use `'cell'` when rendering Select inside a table cell.  */
	variant?: 'standard' | 'cell';
	status?: TextFieldStatus | 'loading';
	renderInput?: (params: AutocompleteRenderInputParams) => React.ReactNode;
	placeholder?: string;
	getChipLabel?: (option: T | AutocompleteFreeSoloValueMapping<FreeSolo>) => string;
	/** Optional icon to show on the left of each selected chip in multi-select mode. */
	getChipIcon?: (option: T | AutocompleteFreeSoloValueMapping<FreeSolo>) => React.ReactElement | undefined;
	/** Optional palette colour to apply to the icon of each selected chip. Used alongside getChipIcon. */
	getChipIconColor?: (option: T | AutocompleteFreeSoloValueMapping<FreeSolo>) => PaletteColors | undefined;
	/** Custom label formatter for the selected value when the dropdown is closed. Falls back to getOptionLabel when not provided. */
	// getOptionSelectedLabel?: (option: T | AutocompleteFreeSoloValueMapping<FreeSolo>) => string;

	// Custom styles to apply to the dropdown popper element
	popperStyle?: React.CSSProperties | undefined;
	/** Placement of the dropdown popper relative to the Select. */
	popperPlacement?: PopperProps['placement'];
	/** Theme colour for grouped option labels. */
	groupLabelColor?: string;
	/** Additional props merged into the underlying <input> element (e.g. autoComplete, data-* attributes). */
	inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
}

const StyledTextField = styled(TextField)`
	& span.MuiAutocomplete-tag {
		overflow: visible;
	}
`;

export const GridCellTextField = styled(StyledTextField)`
	& .MuiOutlinedInput-notchedOutline {
		border: none;
	}
`;

// End adornments for status
const ValidationIcon = ({ status, style }: { status: string; style?: React.CSSProperties }) => {
	if (!status) return <></>;
	switch (status) {
		case 'error':
			return <ErrorIcon style={style} color="error" />;
		case 'warning':
			return <WarningIcon style={style} color="warning" />;
		case 'success':
			return <CheckCircleIcon style={style} color="success" />;
		case 'loading':
			return <CircularProgress size="1rem" style={style} />;
	}
};

export function mergeEndAdornments(endAdornments, status?: TextFieldStatus | 'loading', explicitEndAdornment?) {
	const children = endAdornments ? React.Children.toArray(endAdornments?.props?.children) : [];
	if (status && !explicitEndAdornment)
		children.push(<ValidationIcon key={'validation_icon_adornment'} status={status} style={{ verticalAlign: 'middle' }} />);
	if (explicitEndAdornment) children.push(explicitEndAdornment);
	return React.cloneElement(endAdornments || <></>, {}, children || <></>);
}

/**
 * This is a simplification of the Mui Autocomplete component with a few added features. Utilize __Select__ when presenting users with a list of options from which they can select one or more.
Select empowers users to effortlessly filter and select items by typing, while also providing the expected click-based selection behavior.
 */
export function Select<
	T,
	Multiple extends boolean | undefined = false,
	DisableClearable extends boolean | undefined = false,
	FreeSolo extends boolean | undefined = false
>({
	label,
	status,
	helperText,
	variant,
	startAdornment,
	placeholder,
	getChipLabel,
	getChipIcon,
	getChipIconColor,
	required,
	endAdornment,
	inputRef,
	popperStyle,
	popperPlacement,
	groupLabelColor,
	inputProps: extraInputProps,
	...rest
}: SelectProps<T, Multiple, DisableClearable, FreeSolo>) {
	const { contextValue, isOpen, onOpen, onClose } = useMuiClickToDismiss(rest.open, rest.onClose, rest.onOpen);
	const container = useContext(ContainerContext);
	const inGridCell = variant === 'cell';

	const T = inGridCell ? GridCellTextField : StyledTextField;
	return (
		<ClickToDismissPortalContext.Provider value={contextValue}>
			<MuiAutocomplete
				{...rest}
				open={isOpen}
				onClose={onClose}
				onOpen={onOpen}
				renderTags={(value, getTagProps, ownerState) => {
					return value.map((option, index) => {
						const { key, ...rest } = getTagProps({ index });
						const label = getChipLabel ? getChipLabel(option) : typeof option === 'string' ? option : ownerState.getOptionLabel?.(option);
						const icon = getChipIcon?.(option);
						const iconColor = getChipIconColor?.(option);
						return (
							<Chip
								label={label}
								title={label}
								{...rest}
								key={key}
								size="small"
								icon={icon}
								sx={iconColor ? { '& .MuiChip-icon': { color: (theme: Theme) => theme.palette[iconColor].dark } } : undefined}
							/>
						);
					});
				}}
				forcePopupIcon={rest.forcePopupIcon}
				disableClearable={rest.disableClearable}
				componentsProps={{
					popper: {
						container: container,
						...(popperStyle ? { style: popperStyle } : {}),
						...(popperPlacement ? { placement: popperPlacement } : {}),
						...(groupLabelColor ? { sx: { '& .MuiAutocomplete-groupLabel': { color: groupLabelColor } } } : {})
					}
				}}
				renderInput={inputProps => (
					<T
						{...inputProps}
						inputRef={inputRef}
						inputProps={{ ...inputProps.inputProps, ...extraInputProps }}
						required={required}
						placeholder={placeholder}
						status={status === 'loading' ? undefined : status}
						hideValidationIcon
						startAdornment={startAdornment ?? inputProps.InputProps.startAdornment}
						helperText={inGridCell ? null : helperText}
						InputProps={{
							...inputProps.InputProps,
							endAdornment: mergeEndAdornments(inputProps.InputProps.endAdornment, status, endAdornment),
							style: inGridCell ? { flexWrap: 'nowrap' } : undefined
						}}
						label={inGridCell ? undefined : label}
					/>
				)}
			/>
		</ClickToDismissPortalContext.Provider>
	);
}
